import { IQuickVideo, QuickVideoSortKeys } from 'lib/api/quickVideo/types';
import {
  CheckboxInput,
  Switch,
  Table,
  TableContextProvider,
  TableFooter,
  TablePaginationNew,
  TablePaginationSizeNew,
} from 'lib/components';
import React, { useState } from 'react';
import { Button } from 'react-covideo-common';
import {
  Layout,
  PaginationWrapper,
  TableCell,
} from 'app/pages/admin/index.styled';
import { useTheme } from 'styled-components/macro';
import { MdDeleteForever, MdEdit } from 'react-icons/md';
import { SortButtons } from 'app/pages/admin/components/SortButtons';
import { PaginationConstants } from 'lib/const/PaginationConstants';
import DeleteQuickVideoModal from '../modals/DeleteQuickVideoModal';
import { usePatchEnabledStatusMutation } from 'lib/api/quickVideo/usePatchEnabledStatusMutation';
import { getQuickVideoTableFields } from '../main/constants';
import { useHistory } from 'react-router';
import { Flex } from 'lib/components/styles/layout';
import { useTranslation } from 'react-i18next';

type QuickVideosTableProps = {
  data: IQuickVideo[];
  count: number;
  page: number;
  onPaginationChange: ({
    page,
    size,
  }: {
    page: number;
    size: number;
  }) => Promise<void>;
  limit: number;
  order?: PaginationConstants;
  sortKey: QuickVideoSortKeys | null;
  onSortChange?: (
    sortKey: QuickVideoSortKeys,
    order: PaginationConstants
  ) => void;

  selectedQuickVideos: IQuickVideo[];
  setSelectedQuickVideos: React.Dispatch<React.SetStateAction<IQuickVideo[]>>;
};

export const QuickVideosTable = ({
  data,
  count,
  page,
  limit,
  sortKey,
  order,
  selectedQuickVideos,
  onPaginationChange,
  onSortChange,
  setSelectedQuickVideos,
}: QuickVideosTableProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const [quickVideoForDelete, setQuickVideoForDelete] =
    useState<IQuickVideo | null>(null);
  const [enabledQuickVideos, setEnabledQuickVideos] = useState<number[]>(
    data.filter(qv => qv.enabled).map(qv => qv.quickVideoId)
  );

  const quickVideoTableFields = getQuickVideoTableFields(t);
  const {
    mutateAsync: patchEnabledStatusMutation,
    isLoading: isPatchEnableStatusLoading,
  } = usePatchEnabledStatusMutation();

  const columnWidths = [320, 250, 100, 100, 100];

  const updateEnabledStatus = async (
    isEnabled: boolean,
    quickVideo: IQuickVideo
  ) => {
    if (isEnabled) {
      // remove
      setEnabledQuickVideos(prev =>
        prev.filter(id => id !== quickVideo.quickVideoId)
      );
    } else {
      // add
      setEnabledQuickVideos(prev => [...prev, quickVideo.quickVideoId]);
    }
    await patchEnabledStatusMutation({
      quickVideoId: quickVideo.quickVideoId,
      enabled: quickVideo.enabled ? 0 : 1,
    });
  };

  const toggleQuickVideo = (quickVideo: IQuickVideo) => {
    const exists = selectedQuickVideos.find(
      qv => qv.quickVideoId === quickVideo.quickVideoId
    );
    if (exists) {
      // remove
      setSelectedQuickVideos(prev =>
        prev.filter(qv => qv.quickVideoId !== quickVideo.quickVideoId)
      );
    } else {
      // add
      setSelectedQuickVideos(prev => [...prev, quickVideo]);
    }
  };

  const getTableRowData = () => {
    return data.map(quickVideo => {
      const isEnabled = enabledQuickVideos.includes(quickVideo.quickVideoId);
      const isChecked = !!selectedQuickVideos.find(
        qv => qv.quickVideoId === quickVideo.quickVideoId
      );
      return {
        key: `quick-video-${quickVideo.quickVideoId}`,
        columns: [
          <TableCell width={350} style={{ fontSize: '14px', display: 'block' }}>
            <Flex flexDirection='row' alignItems='center' gap='8px'>
              <CheckboxInput
                name={`${quickVideo.quickVideoId}-checkbox`}
                checked={isChecked}
                onChange={() => toggleQuickVideo(quickVideo)}
              />
              {quickVideo.name}
            </Flex>
          </TableCell>,
          <TableCell style={{ fontSize: '14px' }}>
            {quickVideo.category}
          </TableCell>,
          <TableCell>
            <Button
              text={t('Test Send')}
              variant='secondary'
              size='small'
              onClick={() => {
                history.push(
                  `/ai-configuration/quick-videos/${quickVideo.quickVideoId}/test-send`
                );
              }}
            />
          </TableCell>,
          <TableCell>
            <Switch
              disabled={isPatchEnableStatusLoading}
              isOn={isEnabled}
              onColor={theme.colors.primary[100]}
              handleToggle={() => updateEnabledStatus(isEnabled, quickVideo)}
              id={`quick-video-switch-${quickVideo.quickVideoId}`}
            />
          </TableCell>,
          <TableCell gap='8px'>
            <Button
              icon={<MdEdit size={18} />}
              onClick={() => {
                history.push(
                  `/ai-configuration/quick-videos/${quickVideo.quickVideoId}`
                );
              }}
            />
            <Button
              variant='destructive'
              icon={<MdDeleteForever size={18} />}
              onClick={() => setQuickVideoForDelete(quickVideo)}
            />
          </TableCell>,
        ],
        onClick: () => {},
      };
    });
  };

  return (
    <Layout>
      <TableContextProvider
        total={count}
        initPage={page}
        initSize={limit}
        onChange={onPaginationChange}
      >
        <Table
          compact={true}
          relative={false}
          headers={quickVideoTableFields.map((item, index) => {
            return (
              <TableCell
                key={index}
                width={columnWidths[index]}
                onClick={() => {}}
              >
                {item.label}
                {item.sortable && item.sortKey && (
                  <SortButtons
                    isSorted={sortKey === item.sortKey}
                    order={order}
                    onChange={orderValue =>
                      onSortChange && onSortChange(item.sortKey, orderValue)
                    }
                  />
                )}
              </TableCell>
            );
          })}
          rows={getTableRowData()}
        />
        <PaginationWrapper>
          <TableFooter>
            <TablePaginationNew />
            <TablePaginationSizeNew text={t('Show rows:')} />
          </TableFooter>
        </PaginationWrapper>

        {!!quickVideoForDelete && (
          <DeleteQuickVideoModal
            quickVideo={quickVideoForDelete}
            onClose={() => setQuickVideoForDelete(null)}
          />
        )}
      </TableContextProvider>
    </Layout>
  );
};
